import { InteractionType, type PublicClientApplication } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, MsalProvider, useMsal } from '@azure/msal-react';
import { type PropsWithChildren, useEffect, useRef } from 'react';
import { Loader } from '@/components/common';
import { type UserToken, useUserStore } from '@/stores/user_store';

interface AuthProviderProps {
  msalInstance: PublicClientApplication;
}

function ActiveAccountProvider({ children }: PropsWithChildren) {
  const { accounts } = useMsal();
  const setIsAdmin = useUserStore((s) => s.setIsAdmin);
  const setGroups = useUserStore((s) => s.setGroups);
  const previousAccountId = useRef<string | null>(null);

  const account = accounts[0];

  useEffect(() => {
    if (import.meta.env.VITE_ACCESS_TOKEN) return;
    if (account && account.homeAccountId !== previousAccountId.current) {
      previousAccountId.current = account.homeAccountId;
      if (account.idTokenClaims) {
        const token = account.idTokenClaims as UserToken;
        setIsAdmin(token);
        setGroups(token);
      }
    }
  }, [account, setIsAdmin, setGroups]);

  return children;
}

function AccessTokenProvider({ children }: PropsWithChildren) {
  const setIsAdmin = useUserStore((s) => s.setIsAdmin);
  const setGroups = useUserStore((s) => s.setGroups);

  useEffect(() => {
    const payload = JSON.parse(atob(import.meta.env.VITE_ACCESS_TOKEN.split('.')[1]));
    setIsAdmin(payload);
    setGroups(payload);
  }, [setIsAdmin, setGroups]);

  return children;
}

export function AuthProvider({ msalInstance, children }: PropsWithChildren<AuthProviderProps>) {
  if (import.meta.env.VITE_ACCESS_TOKEN) {
    return <AccessTokenProvider>{children}</AccessTokenProvider>;
  }

  const authenticationRequest = {
    scopes: [`api://${import.meta.env.VITE_CLIENT_ID}/user_impersonation`],
  };

  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate
        authenticationRequest={authenticationRequest}
        interactionType={InteractionType.Redirect}
        loadingComponent={Loader}
      >
        <ActiveAccountProvider>{children}</ActiveAccountProvider>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}
